import { signal } from '@angular/core';
import { debounce, FieldTree, min, required, SchemaPathTree } from '@angular/forms/signals';
import { BuriedDto, FactorsIEC60502P2Dto } from '../../../../core/mceapi.client.generated';
import { apiOpt, apiOptSome, appOpt, getNoneOptionDto, getSomeOptionDto, isSomeOptionDto } from '../../../../core/mceapi.client.helpers';
import { match } from '../../../../shared/functions/functional';
import { Grouping, defaultGrouping, buildGroupingSchema, createGrouping, createGroupingDto } from '../grouping/grouping.model';
import { Buried, defaultBuried, buildBuriedSchema, createBuried, createBuriedDto } from '../buried/buried.model';
import { AmbientTemperature, buildAmbientTemperatureSchema, createAmbientTemperature, createAmbientTemperatureDto, defaultAmbientTemperature } from '../ambient-temperature/ambient-temperature.model';
import { UserDefinedFactor, defaultUserDefinedFactor, buildUserDefinedFactorSchema, createUserDefinedFactor, createUserDefinedFactorDto } from '../user-defined-factor/user-defined-factor.model';

export interface FactorsIEC60502P2 {
  primaryMethodGuid: string,
  secondaryMethodGuid: string,
  ambientTemperature: AmbientTemperature,
  grouping: Grouping,
  buried: Buried,
  numberOfTrays: number,
  userDefinedFactor: UserDefinedFactor,
  isBuried: boolean,
  isOnTray: boolean,
}

export function defaultFactorsIEC60502P2(defaultAirTemperature: number, defaultGroundTemperature: number, defaultSoilThermalResistivtiy: number): FactorsIEC60502P2 {
  return {
    primaryMethodGuid: '',
    secondaryMethodGuid: '',
    ambientTemperature: defaultAmbientTemperature(defaultAirTemperature, defaultGroundTemperature),
    grouping: defaultGrouping(),
    buried: defaultBuried(defaultSoilThermalResistivtiy),
    numberOfTrays: 1,
    userDefinedFactor: defaultUserDefinedFactor(),
    isBuried: false,
    isOnTray: false
  };
}

export function buildFactorsIEC60502P2Schema(path: SchemaPathTree<FactorsIEC60502P2>) {

  required(path.numberOfTrays, { message: `Number of trays is required` });
  min(path.numberOfTrays, 1, { message: `Number of trays must be above 1 m` });
  debounce(path.numberOfTrays, 500);

  buildAmbientTemperatureSchema(path.ambientTemperature);
  buildGroupingSchema(path.grouping);
  buildBuriedSchema(path.buried);
  buildUserDefinedFactorSchema(path.userDefinedFactor);
}

export function createFactorsIEC60502P2(dto: FactorsIEC60502P2Dto, defaultSoilThermalResistivtiy: number) {
  const primaryMethodGuid = dto.primaryMethodGuid;
  const secondaryMethodGuid = dto.secondaryMethodGuid;
  const ambientTemperature = dto.ambientTemperature;
  const grouping = dto.grouping;

  if (primaryMethodGuid === undefined
    || secondaryMethodGuid === undefined
    || ambientTemperature === undefined
    || grouping === undefined) {
    throw new Error('IEC 60502-2 factors DTO is missing required values.');
  }

  return signal<FactorsIEC60502P2>({
    primaryMethodGuid,
    secondaryMethodGuid,
    ambientTemperature: createAmbientTemperature(ambientTemperature)(),
    grouping: createGrouping(grouping)(),
    buried:
      match(
        () => ({ depth: 0.9, soilThermalResistivity: defaultSoilThermalResistivtiy }),
        (x: BuriedDto) => createBuried(x)())
        (appOpt(dto.buried ?? {})),
    numberOfTrays:
      match(
        () => 1,
        (x: number) => x)
        (appOpt(dto.numberOfTrays ?? {})),
    userDefinedFactor: createUserDefinedFactor(appOpt(dto.userDefinedFactor ?? {}))(),
    isBuried: isSomeOptionDto(dto.buried ?? {}),
    isOnTray: isSomeOptionDto(dto.numberOfTrays ?? {})
  });
}

export function createFactorsIEC60502P2Dto(model: FieldTree<FactorsIEC60502P2>) {
  return signal<FactorsIEC60502P2Dto>({
    primaryMethodGuid: model.primaryMethodGuid().value(),
    secondaryMethodGuid: model.secondaryMethodGuid().value(),
    ambientTemperature: createAmbientTemperatureDto(model.ambientTemperature)(),
    grouping: createGroupingDto(model.grouping)(),
    buried:
      model.isBuried().value()
        ? getSomeOptionDto<BuriedDto>(createBuriedDto(model.buried)())
        : getNoneOptionDto<BuriedDto>(),
    numberOfTrays:
      model.isOnTray().value()
        ? apiOptSome(model.numberOfTrays().value())
        : getNoneOptionDto<number>(),
    userDefinedFactor: apiOpt(createUserDefinedFactorDto(model.userDefinedFactor)())
  });
}
